import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ChatMessage, PeerInfo, RemotePeer, SignalingMessage } from './types'

interface UseMeshOptions {
  slug: string
  ticket: string
  iceServers: RTCIceServer[]
  localStream: MediaStream | null
  displayName: string
  initialMessages?: ChatMessage[]
  onChat?: (msg: ChatMessage) => void
  onKicked?: (reason?: string) => void
}

interface PeerConnectionEntry {
  pc: RTCPeerConnection
  stream: MediaStream
  info: PeerInfo
  queuedIce: RTCIceCandidateInit[]
  remoteSet: boolean
  state: RTCPeerConnectionState
}

export function useMesh(opts: UseMeshOptions) {
  const { slug, ticket, iceServers, localStream, initialMessages, onChat, onKicked } = opts
  const [selfId, setSelfId] = useState<string | null>(null)
  const [peers, setPeers] = useState<Record<string, RemotePeer>>({})
  const [connected, setConnected] = useState(false)
  const [chat, setChat] = useState<ChatMessage[]>(initialMessages ?? [])

  const wsRef = useRef<WebSocket | null>(null)
  const pcRef = useRef<Map<string, PeerConnectionEntry>>(new Map())
  const localStreamRef = useRef<MediaStream | null>(null)
  const iceServersRef = useRef<RTCIceServer[]>(iceServers)
  const peerInfoRef = useRef<Map<string, PeerInfo>>(new Map())
  const selfIdRef = useRef<string | null>(null)
  const onChatRef = useRef(onChat)
  const onKickedRef = useRef(onKicked)

  // Gate WebSocket on initial media acquisition so createPeer always has local tracks.
  const [mediaReady, setMediaReady] = useState(false)

  // Once: seed chat with initialMessages when they arrive (after async fetch).
  // We never overwrite once at least one server message has been appended.
  const seededRef = useRef(false)
  useEffect(() => {
    if (seededRef.current) return
    if (!initialMessages || initialMessages.length === 0) return
    setChat((prev) => {
      // If we somehow already have some messages, don't clobber them.
      if (prev.length > 0) return prev
      seededRef.current = true
      return initialMessages
    })
  }, [initialMessages])

  useEffect(() => { iceServersRef.current = iceServers }, [iceServers])
  useEffect(() => { onChatRef.current = onChat }, [onChat])
  useEffect(() => { onKickedRef.current = onKicked }, [onKicked])
  useEffect(() => { selfIdRef.current = selfId }, [selfId])
  useEffect(() => {
    localStreamRef.current = localStream
    if (localStream) setMediaReady(true)
  }, [localStream])

  const send = useCallback((msg: Record<string, unknown>) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify(msg))
  }, [])

  const upsertPeer = useCallback((id: string, patch: Partial<RemotePeer>) => {
    setPeers((prev) => {
      const existing = prev[id]
      if (!existing && !patch.info) return prev
      return {
        ...prev,
        [id]: {
          info: patch.info ?? existing?.info ?? {
            id,
            userId: '',
            displayName: '',
            audio: true,
            video: true,
            screen: false,
          },
          stream: patch.stream !== undefined ? patch.stream : existing?.stream ?? null,
          connectionState:
            patch.connectionState ?? existing?.connectionState ?? 'new',
        },
      }
    })
  }, [])

  const removePeer = useCallback((id: string) => {
    const entry = pcRef.current.get(id)
    if (entry) {
      try { entry.pc.close() } catch { /* ignore */ }
      pcRef.current.delete(id)
    }
    peerInfoRef.current.delete(id)
    setPeers((prev) => {
      if (!prev[id]) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
  }, [])

  // Creates RTCPeerConnection with event handlers. Does NOT add local tracks —
  // tracks are added by the offerer before createOffer, or by the answerer
  // after setRemoteDescription (so ontrack fires before our tracks arrive).
  const makePc = useCallback((info: PeerInfo): PeerConnectionEntry => {
    const existing = pcRef.current.get(info.id)
    if (existing) return existing

    const pc = new RTCPeerConnection({ iceServers: iceServersRef.current })
    const stream = new MediaStream()

    pc.ontrack = (e) => {
      const track = e.track
      if (!stream.getTrackById(track.id)) stream.addTrack(track)
      // New MediaStream reference so React's useEffect([stream]) in VideoTile re-runs.
      upsertPeer(info.id, { stream: new MediaStream(stream.getTracks()) })
    }

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        send({ type: 'ice', to: info.id, payload: e.candidate.toJSON() })
      }
    }

    pc.onconnectionstatechange = () => {
      upsertPeer(info.id, { connectionState: pc.connectionState })
      const entry = pcRef.current.get(info.id)
      if (entry) entry.state = pc.connectionState
    }

    const entry: PeerConnectionEntry = {
      pc,
      stream,
      info,
      queuedIce: [],
      remoteSet: false,
      state: 'new',
    }
    pcRef.current.set(info.id, entry)
    upsertPeer(info.id, { info })
    return entry
  }, [send, upsertPeer])

  // Add local camera/mic tracks to a peer connection, skipping kinds already present.
  const addLocalTracks = useCallback((pc: RTCPeerConnection) => {
    const local = localStreamRef.current
    if (!local) return
    const existingKinds = new Set(pc.getSenders().map((s) => s.track?.kind).filter(Boolean))
    for (const track of local.getTracks()) {
      if (!existingKinds.has(track.kind)) {
        pc.addTrack(track, local)
      }
    }
  }, [])

  // OFFERER: add local tracks first so they appear in the offer SDP.
  const offerTo = useCallback(
    async (info: PeerInfo) => {
      const entry = makePc(info)
      addLocalTracks(entry.pc)
      const offer = await entry.pc.createOffer()
      await entry.pc.setLocalDescription(offer)
      send({ type: 'offer', to: info.id, payload: offer })
    },
    [makePc, addLocalTracks, send],
  )

  // ANSWERER: setRemoteDescription FIRST so ontrack fires and captures remote tracks,
  // then add local tracks so they reuse the offer's transceivers in the answer.
  const handleOffer = useCallback(
    async (from: string, sdp: RTCSessionDescriptionInit) => {
      const knownInfo = peerInfoRef.current.get(from)
      const info: PeerInfo = knownInfo ?? {
        id: from,
        userId: '',
        displayName: 'Peer',
        audio: true,
        video: true,
        screen: false,
      }
      const entry = pcRef.current.get(from) ?? makePc(info)

      await entry.pc.setRemoteDescription(new RTCSessionDescription(sdp))
      entry.remoteSet = true

      for (const c of entry.queuedIce) {
        try { await entry.pc.addIceCandidate(c) } catch { /* ignore */ }
      }
      entry.queuedIce = []

      addLocalTracks(entry.pc)

      const answer = await entry.pc.createAnswer()
      await entry.pc.setLocalDescription(answer)
      send({ type: 'answer', to: from, payload: answer })
    },
    [makePc, addLocalTracks, send],
  )

  const handleAnswer = useCallback(
    async (from: string, sdp: RTCSessionDescriptionInit) => {
      const entry = pcRef.current.get(from)
      if (!entry) return
      await entry.pc.setRemoteDescription(new RTCSessionDescription(sdp))
      entry.remoteSet = true
      for (const c of entry.queuedIce) {
        try { await entry.pc.addIceCandidate(c) } catch { /* ignore */ }
      }
      entry.queuedIce = []
    },
    [],
  )

  const handleIce = useCallback(async (from: string, c: RTCIceCandidateInit) => {
    const entry = pcRef.current.get(from)
    if (!entry) return
    if (!entry.remoteSet) {
      entry.queuedIce.push(c)
      return
    }
    try { await entry.pc.addIceCandidate(c) } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    if (!ticket || !slug || !mediaReady) return
    const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const url = `${scheme}//${window.location.host}/api/ws/rooms/${encodeURIComponent(slug)}?ticket=${encodeURIComponent(ticket)}`

    const ws = new WebSocket(url)
    wsRef.current = ws

    ws.onopen = () => setConnected(true)
    ws.onclose = () => setConnected(false)
    ws.onerror = () => setConnected(false)

    ws.onmessage = async (ev) => {
      let msg: SignalingMessage
      try {
        msg = JSON.parse(ev.data) as SignalingMessage
      } catch {
        return
      }
      switch (msg.type) {
        case 'hello': {
          setSelfId(msg.peerId)
          selfIdRef.current = msg.peerId
          for (const info of msg.peers) {
            peerInfoRef.current.set(info.id, info)
            await offerTo(info)
          }
          return
        }
        case 'peer-joined': {
          peerInfoRef.current.set(msg.peer.id, msg.peer)
          upsertPeer(msg.peer.id, { info: msg.peer })
          return
        }
        case 'peer-left': {
          removePeer(msg.peerId)
          return
        }
        case 'presence': {
          upsertPeer(msg.peer.id, { info: msg.peer })
          return
        }
        case 'offer':
          await handleOffer(msg.from, msg.payload)
          return
        case 'answer':
          await handleAnswer(msg.from, msg.payload)
          return
        case 'ice':
          await handleIce(msg.from, msg.payload)
          return
        case 'chat': {
          const item: ChatMessage = {
            id: msg.id,
            from: msg.from,
            displayName: msg.displayName,
            text: msg.text,
            ts: msg.ts,
            self: msg.from === selfIdRef.current,
          }
          setChat((prev) => {
            // Skip if we somehow received the same id twice (server retry, etc.)
            if (prev.some((m) => m.id === item.id)) return prev
            return [...prev, item].slice(-500)
          })
          onChatRef.current?.(item)
          return
        }
        case 'chat-cleared': {
          setChat([])
          return
        }
        case 'kicked': {
          onKickedRef.current?.(msg.reason)
          try { ws.close() } catch { /* ignore */ }
          return
        }
      }
    }

    return () => {
      try { ws.close() } catch { /* ignore */ }
      for (const [, entry] of pcRef.current) {
        try { entry.pc.close() } catch { /* ignore */ }
      }
      pcRef.current.clear()
      peerInfoRef.current.clear()
      setPeers({})
      setSelfId(null)
      selfIdRef.current = null
      setConnected(false)
    }
  }, [slug, ticket, mediaReady, offerTo, handleOffer, handleAnswer, handleIce, upsertPeer, removePeer])

  // Replace tracks when local stream changes (device switch).
  useEffect(() => {
    if (!localStream) return
    for (const [, entry] of pcRef.current) {
      const senders = entry.pc.getSenders()
      for (const track of localStream.getTracks()) {
        const sender = senders.find((s) => s.track?.kind === track.kind)
        if (sender) {
          void sender.replaceTrack(track)
        } else {
          entry.pc.addTrack(track, localStream)
        }
      }
    }
  }, [localStream])

  const sendPresence = useCallback(
    (p: { audio?: boolean; video?: boolean; screen?: boolean }) => {
      send({ type: 'presence', ...p })
    },
    [send],
  )

  // No optimistic add — the server persists the message and echoes it back to
  // everyone (including the sender). The chat handler marks `self` via
  // `from === selfId`. This eliminates the duplicate-own-message bug.
  const sendChat = useCallback(
    (text: string) => {
      if (!text.trim()) return
      const trimmed = text.slice(0, 4000)
      send({ type: 'chat', text: trimmed })
    },
    [send],
  )

  // Locally drop the chat list (used after an explicit clear via the REST API
  // — the server-side broadcast may not reach the caller before they navigate
  // away from the panel, so we apply it optimistically here too).
  const clearChatLocally = useCallback(() => {
    setChat([])
  }, [])

  const peerList = useMemo(() => Object.values(peers), [peers])

  return { selfId, connected, peers: peerList, chat, sendPresence, sendChat, clearChatLocally }
}
